import React, { useEffect, useMemo, useState } from 'react';

// Reusable list-table controls: a search box, per-column filter dropdowns and
// pagination. Pages keep their own <table> markup and just map over `dt.rows`,
// rendering <TableToolbar dt={dt} /> above and <TablePager dt={dt} /> below.
//
//   const dt = useDataTable(rows, {
//     searchKeys: ['name', 'category', (r) => r.vendor?.name],   // matched by the search box
//     filters: [{ key: 'category', label: 'Category', value: (r) => r.category }],
//     columns: [                                    // per-column boxes under the headings
//       { key: 'name', value: (r) => r.name },      //   text: matches anywhere in the value
//       { key: 'cat', value: (r) => r.category, type: 'select' },   // select: the distinct values
//     ],
//     pageSize: 25,
//     searchPlaceholder: 'Search products…',
//   });
//   …<TableFilterRow dt={dt} cells={['name', 'cat', null, null]} />  inside <thead>
//   …<TableToolbar dt={dt} right={<button/>} />  {dt.rows.map(...)}  <TablePager dt={dt} />
//
// `filters`/`searchKeys` may be inline arrays — the hook re-derives cheaply.

const PAGE_SIZES = [10, 25, 50, 100];

export function useDataTable(rows, opts = {}) {
  const { searchKeys = [], filters = [], columns = [], pageSize = 25, searchPlaceholder } = opts;
  const [q, setQ] = useState('');
  const [active, setActive] = useState({});   // filterKey -> selected value ('' = all)
  const [cols, setCols] = useState({});       // column key -> typed text / chosen value
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(pageSize);

  const setSearch = (v) => { setQ(v); setPage(1); };
  const setFilter = (key, v) => { setActive((a) => ({ ...a, [key]: v })); setPage(1); };
  const setColumn = (key, v) => { setCols((c) => ({ ...c, [key]: v })); setPage(1); };
  const clear = () => { setQ(''); setActive({}); setCols({}); setPage(1); };
  const isFiltered = !!q || Object.values(active).some((v) => v) || Object.values(cols).some((v) => v);

  // Distinct dropdown options per filter, from the full (unfiltered) row set.
  const distinct = (defs) => {
    const o = {};
    for (const f of defs) {
      const set = new Set();
      for (const r of rows) { const v = f.value(r); if (v != null && v !== '') set.add(String(v)); }
      o[f.key] = Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    }
    return o;
  };
  const options = useMemo(() => distinct(filters), [rows, filters]);   // eslint-disable-line react-hooks/exhaustive-deps
  const colOptions = useMemo(() => distinct(columns.filter((c) => c.type === 'select')), [rows, columns]);   // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (needle) {
        const hay = searchKeys.map((k) => (typeof k === 'function' ? k(r) : r[k]));
        if (!hay.some((x) => String(x ?? '').toLowerCase().includes(needle))) return false;
      }
      for (const f of filters) {
        const sel = active[f.key];
        if (sel != null && sel !== '' && String(f.value(r) ?? '') !== String(sel)) return false;
      }
      // A column box: a select must match exactly, text matches anywhere in the
      // value — the way someone types half a bill number to find it.
      for (const c of columns) {
        const v = cols[c.key];
        if (v == null || v === '') continue;
        const cell = String(c.value(r) ?? '');
        if (c.type === 'select' ? cell !== String(v) : !cell.toLowerCase().includes(String(v).toLowerCase())) return false;
      }
      return true;
    });
  }, [rows, q, active, cols, searchKeys, filters, columns]);

  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / size));
  useEffect(() => { if (page > pages) setPage(pages); }, [page, pages]);
  const start = (page - 1) * size;
  const paged = filtered.slice(start, start + size);

  return {
    q, setSearch, active, setFilter, clear, isFiltered, options, filters, searchPlaceholder,
    cols, setColumn, columns, colOptions,
    rows: paged, filtered, total, allTotal: rows.length,
    page, setPage, pages, size, setSize, start,
  };
}

export function TableToolbar({ dt, right }) {
  return (
    <div className="dt-toolbar">
      <input className="dt-search" placeholder={dt.searchPlaceholder || 'Search…'} value={dt.q} onChange={(e) => dt.setSearch(e.target.value)} />
      {dt.filters.map((f) => (
        <select key={f.key} className="dt-filter" value={dt.active[f.key] || ''} onChange={(e) => dt.setFilter(f.key, e.target.value)}>
          <option value="">{f.label || f.key}: All</option>
          {(dt.options[f.key] || []).map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
      ))}
      {dt.isFiltered && <button className="btn btn-outline btn-sm dt-clear" onClick={dt.clear}>Clear</button>}
      {right && <div className="dt-toolbar-right">{right}</div>}
    </div>
  );
}

// A filter row for inside <thead>: one cell per column of the table, so the box
// sits under the heading it filters rather than in a toolbar the eye has to map
// back onto the columns.
export function TableFilterRow({ dt, cells }) {
  const defs = Object.fromEntries((dt.columns || []).map((c) => [c.key, c]));
  return (
    <tr className="dt-filter-row">
      {cells.map((cell, i) => {
        const key = typeof cell === 'string' ? cell : null;
        const def = key ? defs[key] : null;
        if (!def) return <th key={i} />;
        const value = dt.cols[key] || '';
        return (
          <th key={i}>
            {def.type === 'select' ? (
              <select value={value} onChange={(e) => dt.setColumn(key, e.target.value)} aria-label={def.label || key}>
                <option value="">All</option>
                {(dt.colOptions[key] || []).map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            ) : (
              <input value={value} onChange={(e) => dt.setColumn(key, e.target.value)}
                placeholder={def.placeholder || 'Filter…'} aria-label={def.label || key} />
            )}
          </th>
        );
      })}
    </tr>
  );
}

export function TablePager({ dt }) {
  if (dt.allTotal === 0) return null;
  const { page, pages, total, start, size } = dt;
  const from = total === 0 ? 0 : start + 1;
  const to = Math.min(start + size, total);
  return (
    <div className="dt-pager">
      <span className="muted small">
        {from}–{to} of {total}{dt.filtered.length !== dt.allTotal ? ` · filtered from ${dt.allTotal}` : ''}
      </span>
      <div className="dt-pager-ctrls">
        <select value={size} onChange={(e) => dt.setSize(Number(e.target.value))} title="Rows per page">
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}/page</option>)}
        </select>
        <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => dt.setPage(1)} title="First">«</button>
        <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => dt.setPage(page - 1)} title="Previous">‹</button>
        <span className="dt-pageno">{page} / {pages}</span>
        <button className="btn btn-outline btn-sm" disabled={page >= pages} onClick={() => dt.setPage(page + 1)} title="Next">›</button>
        <button className="btn btn-outline btn-sm" disabled={page >= pages} onClick={() => dt.setPage(pages)} title="Last">»</button>
      </div>
    </div>
  );
}

// ── Holding a row while you read across it ──────────────────────────────────
// A finance table is read sideways — a bill through to what is still owed on it
// — and the eye loses the line halfway. Clicking a row holds it; clicking again
// lets it go. Several can be held at once, which is how people compare two
// entries that are nowhere near each other in the list.
export function useRowPick() {
  const [picked, setPicked] = useState(() => new Set());
  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (!next.delete(id)) next.add(id);
    return next;
  });
  const clear = () => setPicked(new Set());
  // Spread onto a <tr>. Anything with its own click — a link, a button — has to
  // stop the event, or holding a row would also open whatever was under it.
  const rowProps = (id) => ({
    className: picked.has(id) ? 'row-picked' : undefined,
    onClick: () => toggle(id),
  });
  // For tables whose rows already do something when clicked — opening a bill,
  // say. The row is marked but only the frozen cell holds it, so the two
  // gestures do not fight.
  const markProps = (id) => ({ className: picked.has(id) ? 'row-picked' : undefined });
  const cellProps = (id, className = 'freeze-col') => ({
    className,
    onClick: (e) => { e.stopPropagation(); toggle(id); },
    title: 'Click to highlight this row',
  });
  return { picked, toggle, clear, rowProps, markProps, cellProps, count: picked.size };
}

// A "n held ✕" chip for the toolbar, rendered only when something is held.
export function PickedChip({ pick }) {
  if (!pick.count) return null;
  return (
    <button className="btn btn-outline btn-sm" onClick={pick.clear} title="Clear the highlighted rows">
      {pick.count} highlighted ✕
    </button>
  );
}
